import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, FalhaDaApi } from '../api/cliente';
import {
  CabecalhoDaPagina,
  Carregando,
  Cartao,
  Ficha,
  Indicador,
  Vazio,
} from '../componentes/base';
import { Campo, Seletor } from '../componentes/campos';
import { ModalDarBaixa, type ParcelaParaBaixar } from '../componentes/DarBaixa';
import { MenuAcoes } from '../componentes/MenuAcoes';
import { Paginacao, type Paginado } from '../componentes/Paginacao';
import { ValorPagoEditavel } from '../componentes/ValorPagoEditavel';
import { data, mesPorExtenso, moeda, percentual, plural } from '../util/formato';

interface Parcela {
  readonly id: string;
  readonly numero: number;
  readonly valor: number;
  readonly vencimento: string;
  readonly status: string;
  readonly devedor: {
    id: string;
    nome: string;
    clinica: { id: string; nome: string; percentualHonorarios?: number };
  };
  readonly acordo: { quantidadeParcelas: number };
  readonly pagamento: {
    valorPago: number;
    honorarios: number;
    permaneceComClinica: number;
    percentualHonorarios: number;
    dataPagamento: string;
    formaPagamento: string;
    observacoes?: string | null;
    comprovante: string | null;
  } | null;
}

function mesAtual(): string {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
}

export function PaginaPagamentos() {
  const clienteConsulta = useQueryClient();
  const [pagina, setPagina] = useState(1);
  const [corrigindo, setCorrigindo] = useState<{
    parcela: ParcelaParaBaixar;
    devedorId: string;
    nome: string;
    percentualHonorarios: number;
  } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [params, setParams] = useSearchParams();
  const mes = params.get('mes') ?? mesAtual();
  const clinicaId = params.get('clinicaId') ?? '';

  function definirFiltro(chave: 'mes' | 'clinicaId', valor: string) {
    const novo = new URLSearchParams(params);
    if (valor) novo.set(chave, valor);
    else novo.delete(chave);
    setParams(novo, { replace: true });
    setPagina(1);
  }

  const { data: clinicas } = useQuery({
    queryKey: ['clinicas', 'selecao'],
    queryFn: () =>
      api.buscar<Array<{ id: string; nome: string; percentualHonorarios?: number }>>(
        '/clinicas/selecao',
      ),
  });

  const { data: lista, isLoading } = useQuery({
    queryKey: ['pagamentos', pagina, mes, clinicaId],
    queryFn: () =>
      api.buscar<Paginado<Parcela>>('/parcelas', {
        pagina,
        limite: 50,
        status: 'PAGO',
        mesPagamento: mes,
        ...(clinicaId ? { clinicaId } : {}),
      }),
  });

  const excluir = useMutation({
    mutationFn: (parcelaId: string) =>
      api.criar(`/parcelas/${parcelaId}/estorno`, {
        motivo: 'Baixa excluída pela equipe — lançamento incorreto.',
      }),
    onSuccess: () => {
      setAviso('Baixa excluída.');
      void clienteConsulta.invalidateQueries({ queryKey: ['pagamentos'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['painel'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['devedores'] });
    },
    onError: (e) =>
      setAviso(e instanceof FalhaDaApi ? e.message : 'Não foi possível excluir a baixa.'),
  });

  const itens = lista?.itens ?? [];
  const totais = itens.reduce(
    (soma, p) => ({
      recuperado: soma.recuperado + (p.pagamento?.valorPago ?? 0),
      honorarios: soma.honorarios + (p.pagamento?.honorarios ?? 0),
      semComprovante: soma.semComprovante + (p.pagamento?.comprovante ? 0 : 1),
    }),
    { recuperado: 0, honorarios: 0, semComprovante: 0 },
  );

  return (
    <>
      <CabecalhoDaPagina
        titulo="Pagamentos recebidos"
        descricao={`Baixas registradas em ${mesPorExtenso(mes).toLowerCase()} — creditadas direto na conta da clínica`}
      />

      {aviso && (
        <p className="mb-4 text-sm text-[color:var(--texto-suave)]" role="status">
          {aviso}
        </p>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Indicador
          rotulo="Recuperado na página"
          valor={moeda(totais.recuperado)}
          nota={plural(itens.length, 'pagamento', 'pagamentos')}
          tom="recebido"
        />
        <Indicador
          rotulo="Honorários do escritório"
          valor={moeda(totais.honorarios)}
          nota="congelados na data de cada baixa"
          tom="acento"
        />
        <Indicador
          rotulo="Sem comprovante"
          valor={String(totais.semComprovante)}
          nota="pendência a resolver antes de faturar"
          tom={totais.semComprovante > 0 ? 'atencao' : 'neutro'}
        />
      </div>

      <Cartao semPadding>
        <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-3">
          <Campo
            rotulo="Mês do pagamento"
            tipo="month"
            valor={mes}
            aoMudar={(v) => definirFiltro('mes', v)}
          />
          <Seletor
            rotulo="Clínica"
            valor={clinicaId}
            aoMudar={(v) => definirFiltro('clinicaId', v)}
            vazio="Todas"
            opcoes={(clinicas ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))}
          />
        </div>

        {isLoading ? (
          <Carregando />
        ) : itens.length === 0 ? (
          <Vazio titulo={`Nenhum pagamento registrado em ${mesPorExtenso(mes).toLowerCase()}.`} />
        ) : (
          <>
            <div className="rolagem-lateral border-t">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-[color:var(--superficie-afundada)] text-left text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
                    <th className="px-5 py-2.5 font-medium">Devedor</th>
                    <th className="px-3 py-2.5 font-medium">Parcela</th>
                    <th className="px-3 py-2.5 font-medium">Pago em</th>
                    <th className="px-3 py-2.5 text-right font-medium">Valor</th>
                    <th className="px-3 py-2.5 text-right font-medium">Honorários</th>
                    <th className="px-3 py-2.5 text-right font-medium">Fica com a clínica</th>
                    <th className="px-5 py-2.5 font-medium">Comprovante</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((p) => (
                    <tr key={p.id} className="border-b last:border-0">
                      <td className="px-5 py-3">
                        <Link
                          to={`/devedores/${p.devedor.id}`}
                          className="font-medium text-[color:var(--texto)] hover:text-[color:var(--acento)]"
                        >
                          {p.devedor.nome}
                        </Link>
                        <p className="text-[0.6875rem] text-[color:var(--texto-tenue)]">
                          {p.devedor.clinica.nome}
                        </p>
                      </td>
                      <td className="tabular px-3 py-3 whitespace-nowrap text-[color:var(--texto-suave)]">
                        {p.numero === 0
                          ? 'Entrada'
                          : `${p.numero}/${p.acordo.quantidadeParcelas}`}
                      </td>
                      <td className="tabular px-3 py-3 whitespace-nowrap text-[color:var(--texto-suave)]">
                        {data(p.pagamento?.dataPagamento)}
                      </td>
                      <td className="tabular px-3 py-3 text-right whitespace-nowrap">
                        {p.pagamento ? (
                          <ValorPagoEditavel
                            parcelaId={p.id}
                            valorPago={p.pagamento.valorPago}
                            devedorId={p.devedor.id}
                          />
                        ) : (
                          moeda(0)
                        )}
                      </td>
                      <td
                        className="tabular px-3 py-3 text-right font-medium whitespace-nowrap"
                        style={{ color: 'var(--acento)' }}
                      >
                        {moeda(p.pagamento?.honorarios ?? 0)}
                        <span className="ml-1 text-[0.6875rem] font-normal text-[color:var(--texto-tenue)]">
                          {percentual(p.pagamento?.percentualHonorarios ?? 0)}
                        </span>
                      </td>
                      <td className="tabular px-3 py-3 text-right whitespace-nowrap text-[color:var(--texto-suave)]">
                        {moeda(p.pagamento?.permaneceComClinica ?? 0)}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          {p.pagamento?.comprovante ? (
                            <Ficha tom="recebido">Anexado</Ficha>
                          ) : (
                            <Ficha tom="atencao">Faltando</Ficha>
                          )}
                          {p.pagamento && (
                            <MenuAcoes
                              rotulo={`Ações da baixa de ${p.devedor.nome}`}
                              itens={[
                                {
                                  rotulo: 'Corrigir',
                                  aoClicar: () =>
                                    setCorrigindo({
                                      parcela: {
                                        id: p.id,
                                        numero: p.numero,
                                        valor: p.valor,
                                        vencimento: p.vencimento,
                                        pagamento: {
                                          valorPago: p.pagamento!.valorPago,
                                          dataPagamento: p.pagamento!.dataPagamento,
                                          formaPagamento: p.pagamento!.formaPagamento,
                                          observacoes: p.pagamento!.observacoes ?? null,
                                          percentualHonorarios:
                                            p.pagamento!.percentualHonorarios,
                                        },
                                      },
                                      devedorId: p.devedor.id,
                                      nome: p.devedor.nome,
                                      percentualHonorarios:
                                        p.devedor.clinica.percentualHonorarios ??
                                        p.pagamento!.percentualHonorarios,
                                    }),
                                },
                                {
                                  rotulo: 'Excluir',
                                  perigo: true,
                                  aoClicar: () => {
                                    if (
                                      window.confirm(
                                        `Excluir a baixa de ${p.devedor.nome} (${moeda(p.pagamento!.valorPago)})? A parcela volta a ficar em aberto.`,
                                      )
                                    ) {
                                      excluir.mutate(p.id);
                                    }
                                  },
                                },
                              ]}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {lista && (
              <Paginacao
                dados={lista}
                aoMudarPagina={setPagina}
                nomeDoItem={['pagamento', 'pagamentos']}
              />
            )}
          </>
        )}
      </Cartao>

      <p className="mt-4 text-xs text-[color:var(--texto-tenue)]">
        "Fica com a clínica" é informativo, para conferência. O dinheiro nunca passa pelo
        escritório — os honorários são cobrados no fechamento mensal.
      </p>

      <ModalDarBaixa
        aberto={corrigindo != null}
        aoFechar={() => setCorrigindo(null)}
        parcela={corrigindo?.parcela ?? null}
        devedorId={corrigindo?.devedorId ?? ''}
        nomeDoDevedor={corrigindo?.nome ?? ''}
        percentualHonorarios={corrigindo?.percentualHonorarios ?? 0}
      />
    </>
  );
}
